import { useState } from "react";
import { useObjectionLibrary, useDeleteObjection, useUpdateObjection } from "@/hooks/useObjectionLibrary";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Trash2, Edit, MessageSquare, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { AddObjectionDialog } from "./AddObjectionDialog";

const categoryColors: Record<string, string> = {
  price: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  timing: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  competitor: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  authority: "bg-green-500/20 text-green-400 border-green-500/30",
  need: "bg-red-500/20 text-red-400 border-red-500/30",
  trust: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
};

export function ObjectionLibraryManager() {
  const { data: objections, isLoading } = useObjectionLibrary();
  const deleteObjection = useDeleteObjection();
  const updateObjection = useUpdateObjection();

  const handleToggleActive = (id: string, currentState: boolean) => {
    updateObjection.mutate({ id, is_active: !currentState });
  };

  const groupedByCategory = objections?.reduce((acc, obj) => {
    if (!acc[obj.category]) {
      acc[obj.category] = [];
    }
    acc[obj.category].push(obj);
    return acc;
  }, {} as Record<string, typeof objections>);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Objection Library</h2>
          <p className="text-muted-foreground">
            AI-powered responses for handling sales objections
          </p>
        </div>
        <AddObjectionDialog />
      </div>

      {Object.entries(groupedByCategory || {}).map(([category, items]) => (
        <div key={category} className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge 
              variant="outline" 
              className={categoryColors[category] || ""}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {items?.length} {items?.length === 1 ? "handler" : "handlers"}
            </span>
          </div>

          <div className="grid gap-3">
            {items?.map((objection) => (
              <Card key={objection.id} className={!objection.is_active ? "opacity-60" : ""}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {objection.trigger_keywords.map((keyword, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                      
                      <p className="text-sm">{objection.primary_response}</p>
                      
                      {objection.strategy && (
                        <p className="text-xs text-muted-foreground">
                          Strategy: {objection.strategy}
                        </p>
                      )}

                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {objection.usage_count} uses
                        </span>
                        {objection.success_rate && (
                          <span className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {objection.success_rate}% success
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch
                        checked={objection.is_active}
                        onCheckedChange={() => handleToggleActive(objection.id, objection.is_active)}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={() => deleteObjection.mutate(objection.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {(!objections || objections.length === 0) && (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">No objection handlers yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add common objections and suggested responses to help your sales team
            </p>
            <AddObjectionDialog />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
